/**
 * Content Library — pre-approval review queue for bulk-generated content
 * (reels, stills, designs, draft briefs).
 *
 * Backend: `backend/servers/social/routers/content_library.py` (parallel
 * build). Base path `/api/content-library` (never `/api/social/...`, per
 * `saas-api-path-convention`):
 *
 *   GET  /api/content-library?status=&kind=&client_brand_id=&source=&q=&page=&page_size=
 *   GET  /api/content-library/counts?client_brand_id=&kind=&source=
 *   GET  /api/content-library/{id}
 *   POST /api/content-library/{id}/approve      { notes?, add_to_calendar? }
 *   POST /api/content-library/{id}/reject       { reason? }   -- reason is OPTIONAL
 *   POST /api/content-library/{id}/unreview
 *   POST /api/content-library/{id}/to-calendar  { caption?, platforms?, proposed_publish_at?, proposed_timezone?, variant? }
 *
 * This stage sits BEFORE the existing post-approval queue
 * (`api/approvals.ts`, `routes/social/Approvals.tsx`): an item only becomes
 * calendar-eligible once approved here. "Add to calendar" (or an approve
 * with `add_to_calendar: true`, or any `draft_post`-kind approval) creates a
 * DRAFT post — never scheduled, never auto-approved downstream.
 *
 * Error codes the UI special-cases (409/403 `detail.error_code`):
 * `not_approved`, `already_on_calendar`, `human_required`. See
 * {@link contentLibraryErrorMessage}.
 *
 * @module api/contentLibrary
 */

import { useApiQuery } from "@plerity/dashboard-shared/hooks";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useClientBrandStore } from "../lib/client-brand";
import { isUuid } from "../lib/uuid";
import type {
  ContentLibraryApproveBody,
  ContentLibraryCounts,
  ContentLibraryFilters,
  ContentLibraryItem,
  ContentLibraryListData,
  ContentLibraryRejectBody,
  ContentLibraryToCalendarBody,
  ContentLibraryToCalendarResult,
} from "../types/contentLibrary";
import { apiErrorMessage } from "./apiErrors";
import { client } from "./client";
import { socialKeys } from "./keys";
import { objectResponseGuard } from "./schemas";

/** Default page size for the library grid (contract max is 100). */
export const CONTENT_LIBRARY_PAGE_SIZE = 24;

const brandParam = (brandId: string | null | undefined): Record<string, string> =>
  brandId && isUuid(brandId) ? { client_brand_id: brandId } : {};

/** Query params for the list call — empty filters are dropped, `q` is trimmed,
 * `status: "all"` (or omitted) sends no `status` param (the server's own default = every status). */
export function contentLibraryListParams(
  brandId: string | null,
  filters: ContentLibraryFilters = {}
): Record<string, string | number> {
  const params: Record<string, string | number> = { ...brandParam(brandId) };
  if (filters.status && filters.status !== "all") params.status = filters.status;
  if (filters.kind) params.kind = filters.kind;
  if (filters.source) params.source = filters.source;
  const q = filters.q?.trim();
  if (q) params.q = q;
  params.page = filters.page ?? 1;
  params.page_size = filters.page_size ?? CONTENT_LIBRARY_PAGE_SIZE;
  return params;
}

function countsParams(
  brandId: string | null,
  filters: Pick<ContentLibraryFilters, "kind" | "source"> = {}
): Record<string, string> {
  const params: Record<string, string> = { ...brandParam(brandId) };
  if (filters.kind) params.kind = filters.kind;
  if (filters.source) params.source = filters.source;
  return params;
}

// ── Queries ────────────────────────────────────────────────────────────────────

export function useContentLibrary(
  brandId: string | null,
  filters: ContentLibraryFilters = {},
  options: { enabled?: boolean; keepPrevious?: boolean } = {}
) {
  const params = contentLibraryListParams(brandId, filters);
  return useApiQuery<ContentLibraryListData>(socialKeys.list("content-library", params), "/api/content-library", {
    axiosConfig: { params },
    schema: objectResponseGuard<ContentLibraryListData>(),
    enabled: options.enabled ?? true,
    staleTime: 30_000,
    ...(options.keepPrevious ? { placeholderData: (prev: ContentLibraryListData | undefined) => prev } : {}),
  });
}

export function useContentLibraryCounts(
  brandId: string | null,
  filters: Pick<ContentLibraryFilters, "kind" | "source"> = {}
) {
  const params = countsParams(brandId, filters);
  return useApiQuery<ContentLibraryCounts>(
    socialKeys.list("content-library-counts", params),
    "/api/content-library/counts",
    { axiosConfig: { params }, schema: objectResponseGuard<ContentLibraryCounts>(), staleTime: 30_000 }
  );
}

/** Sidebar badge — pending count, scoped to the globally selected client (same
 * convention as `useInboxUnreadCount`). `0`/`undefined` hides the badge. */
export function useContentLibraryPendingBadge(): number | undefined {
  const selectedBrandId = useClientBrandStore((s) => s.selectedBrandId);
  return useContentLibraryCounts(selectedBrandId).data?.pending;
}

export function useContentLibraryItem(id: string | null) {
  return useApiQuery<ContentLibraryItem>(
    socialKeys.detail("content-library", id ?? "none"),
    `/api/content-library/${id ?? ""}`,
    { enabled: isUuid(id), schema: objectResponseGuard<ContentLibraryItem>() }
  );
}

// ── Mutations ──────────────────────────────────────────────────────────────────

function invalidateContentLibrary(qc: ReturnType<typeof useQueryClient>, id?: string) {
  qc.invalidateQueries({ queryKey: socialKeys.resource("content-library") });
  qc.invalidateQueries({ queryKey: socialKeys.resource("content-library-counts") });
  if (id) qc.invalidateQueries({ queryKey: socialKeys.detail("content-library", id) });
}

export function useApproveContentLibraryItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body?: ContentLibraryApproveBody }) => {
      const res = await client.post<{ data: ContentLibraryItem }>(`/api/content-library/${id}/approve`, body ?? {});
      return res.data.data;
    },
    onSuccess: (data, { id }) => {
      invalidateContentLibrary(qc, id);
      // An approve that also placed a draft (add_to_calendar / draft_post kind) adds a new post.
      if (data.post_id) qc.invalidateQueries({ queryKey: socialKeys.list("posts") });
    },
  });
}

export function useRejectContentLibraryItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body?: ContentLibraryRejectBody }) => {
      const res = await client.post<{ data: ContentLibraryItem }>(`/api/content-library/${id}/reject`, body ?? {});
      return res.data.data;
    },
    onSuccess: (_data, { id }) => {
      invalidateContentLibrary(qc, id);
      invalidateWithdrawnPosts(qc);
    },
  });
}

/** A library reject also pulls the item's not-yet-published posts off Approvals + the calendar (2026-10-18). */
function invalidateWithdrawnPosts(qc: ReturnType<typeof useQueryClient>) {
  for (const name of ["posts", "calendar", "pending-approvals", "approval-counts"]) {
    qc.invalidateQueries({ queryKey: socialKeys.list(name) });
  }
}

export function useUnreviewContentLibraryItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const res = await client.post<{ data: ContentLibraryItem }>(`/api/content-library/${id}/unreview`, {});
      return res.data.data;
    },
    onSuccess: (_data, { id }) => invalidateContentLibrary(qc, id),
  });
}

/** "Add to calendar" — only valid on an approved item; creates (or, if the
 * item is already placed, idempotently returns) one DRAFT post. */
export function useContentLibraryToCalendar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: ContentLibraryToCalendarBody }) => {
      const res = await client.post<{ data: ContentLibraryToCalendarResult }>(
        `/api/content-library/${id}/to-calendar`,
        body
      );
      return res.data.data;
    },
    onSuccess: (_data, { id }) => {
      invalidateContentLibrary(qc, id);
      qc.invalidateQueries({ queryKey: socialKeys.list("posts") });
    },
  });
}

// ── Errors ─────────────────────────────────────────────────────────────────────
//
// The live API's error envelope is TOP-LEVEL (`_shared/error_middleware.py`):
//   { success: false, data: null, error: "<friendly message>",
//     error_code: "human_required" | "not_approved" | "already_on_calendar", details: {...} }
// `createFetcher`'s `normalizeError` (packages/universal/data-access/src/fetcher.ts)
// already promotes BOTH top-level fields onto the rejected error object as
// `.code` (= `error_code`) and `.message` (= `error`) — including for a 403,
// which it additionally promotes to `AuthError` (same `.code`/`.message`
// fields). So `err.code` IS the error_code; no `responseData.detail` digging
// needed here (unlike the FastAPI-`detail`-body callers elsewhere in this app).

/** The server's `error_code` from a failed request (`err.code`, normalized
 * by `createFetcher`), or null. */
export function contentLibraryErrorCode(err: unknown): string | null {
  const code = (err as { code?: unknown } | null)?.code;
  return typeof code === "string" ? code : null;
}

const ERROR_CODE_MESSAGES: Record<string, string> = {
  not_approved: "This item needs to be approved before it can go on the calendar.",
  already_on_calendar: "This item already has a calendar draft.",
  human_required: "Your role can't make this decision — ask a teammate to approve or reject it.",
};

/** A plain-English message for a failed content-library request — the
 * known `error_code`s first (friendlier than the raw message for these
 * three), then the API's own `error` text (already on `err.message`), then
 * `fallback`. */
export function contentLibraryErrorMessage(err: unknown, fallback: string): string {
  const code = contentLibraryErrorCode(err);
  if (code && ERROR_CODE_MESSAGES[code]) return ERROR_CODE_MESSAGES[code];
  return apiErrorMessage(err, fallback);
}
